import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  EnvelopeSimple,
  Ruler,
  Tree,
  ShieldCheck,
  Star,
  CheckCircle,
  ArrowRight,
  MapPin,
  Clock,
  Hammer,
  ChatCircleText,
} from "@phosphor-icons/react";

// PAUL: Byt ut till ditt riktiga telefonnummer när du är redo.
// Just nu är detta en visuell placeholder — formuläret nedan skickar
// leadet direkt till din inbox oavsett.
const CONTACT_PHONE_DISPLAY = "0708-91 40 65";
const CONTACT_PHONE_TEL = "+46708914065";
const CONTACT_EMAIL = "paul@trappkonsulten.se";

const API = process.env.REACT_APP_BACKEND_URL;

const STAIR_TYPES = [
  { value: "rak", label: "Rak trappa" },
  { value: "l-svang", label: "L-sväng" },
  { value: "u-svang", label: "U-sväng" },
  { value: "spiral", label: "Spiraltrappa" },
  { value: "vet_ej", label: "Vet ej — jag vill ha rådgivning" },
];

const MATERIALS = [
  { value: "ek", label: "Ek" },
  { value: "furu", label: "Furu" },
  { value: "ask", label: "Ask" },
  { value: "bok", label: "Bok" },
  { value: "vet_ej", label: "Öppen för förslag" },
];

const TIMEFRAMES = [
  { value: "asap", label: "Så snart som möjligt" },
  { value: "1-3_manader", label: "Inom 1–3 månader" },
  { value: "3-6_manader", label: "Inom 3–6 månader" },
  { value: "flexibel", label: "Flexibel / planerar framåt" },
];

const BENEFITS = [
  {
    icon: Ruler,
    title: "Måttbeställd i Skåne",
    body: "Varje trappa ritas och byggs efter dina exakta mått. Inga standardsatser — vi mäter upp på plats i Malmö, Lund, Helsingborg, Kristianstad och hela Skåne.",
  },
  {
    icon: Tree,
    title: "Massivt svenskt trä",
    body: "Ek, ask, furu eller bok från nordiska skogar. Kvistfritt, torkat och behandlat för att hålla i generationer — inte år.",
  },
  {
    icon: ShieldCheck,
    title: "10 års garanti",
    body: "Vi står för hantverket. 10 års garanti på konstruktion, 25 år på bärande delar. Fri servicekontroll efter första vintern.",
  },
];

const PROCESS = [
  {
    step: "1",
    title: "Kostnadsfritt hembesök",
    body: "Vi kommer hem till dig i Skåne, mäter, lyssnar och ger förslag. Inget säljprat — bara raka besked.",
  },
  {
    step: "2",
    title: "Fast offert inom 48 timmar",
    body: "Du får en tydlig offert med pris, tidsplan och ritning. Ingen dold moms, inga överraskningar.",
  },
  {
    step: "3",
    title: "Tillverkning på verkstan",
    body: "Din trappa byggs i vår verkstad i Skåne. Kvalitetskontroll i varje steg. Du får bilder under produktionen.",
  },
  {
    step: "4",
    title: "Montage & besiktning",
    body: "Vi monterar på 1–2 dagar. Städar efter oss. Går igenom skötselråd och lämnar ett skriftligt besiktningsprotokoll.",
  },
];

const FAQ = [
  {
    q: "Vad kostar en trätrappa i Skåne?",
    a: "En rak måttbeställd ektrappa startar kring 45 000 kr inkl. moms och montage. Priset beror på höjd, material, räcken och specialönskemål. Alla våra offerter är fasta — du får ett exakt pris innan vi börjar bygga.",
  },
  {
    q: "Hur lång tid tar det från beställning till färdig trappa?",
    a: "Från signad offert till monterad trappa: normalt 4–6 veckor. Vi bokar in montage på en tid som passar dig — även kvällar och helger går bra i vissa fall.",
  },
  {
    q: "Vilka delar av Skåne servar ni?",
    a: "Hela Skåne — Malmö, Lund, Helsingborg, Kristianstad, Landskrona, Trelleborg, Ystad, Ängelholm, Höganäs, Simrishamn, Hässleholm, Eslöv och alla orter däremellan. Hembesök är alltid kostnadsfritt inom Skåne.",
  },
  {
    q: "Kan ni bygga trappan så att den passar min befintliga inredning?",
    a: "Ja. Vi matchar färg, oljebehandling och räckesstil mot befintliga golv, dörrar och lister. Ta med bilder eller prover på hembesöket så löser vi det.",
  },
  {
    q: "Har ni ROT-avdrag?",
    a: "Ja — 30 % ROT-avdrag på arbetskostnaden dras direkt på fakturan. Du betalar aldrig ut och söker tillbaka; det sköter vi.",
  },
];

const CITIES = [
  "Malmö", "Lund", "Helsingborg", "Kristianstad", "Landskrona",
  "Trelleborg", "Ystad", "Ängelholm", "Höganäs", "Eslöv",
  "Hässleholm", "Simrishamn", "Staffanstorp", "Vellinge", "Kävlinge",
];

// Enkel Q&A-schema för Google Rich Results — skapas dynamiskt av seed.
function buildFaqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

function buildLocalBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Trappkonsulten — Trätrappor Skåne",
    description:
      "Måttbeställda trätrappor i Skåne. Ek, furu, ask och bok. 10 års garanti, ROT-avdrag och fast pris.",
    telephone: CONTACT_PHONE_TEL,
    email: CONTACT_EMAIL,
    areaServed: CITIES.map((c) => ({ "@type": "City", name: c })),
    priceRange: "45000–150000 SEK",
    url: "https://app.trappkonsulten.se/tratrappor-skane",
  };
}

export default function TratrapporSkane() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postal_code: "",
    city: "",
    stair_type: "vet_ej",
    material: "vet_ej",
    timeframe: "flexibel",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  // Sätt <title> + meta description dynamiskt utan att blanda in helmet-libbar.
  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "Trätrappor Skåne — Måttbeställd trappa med 10 års garanti | Trappkonsulten";
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", name);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Måttbeställda trätrappor i Skåne. Ek, ask, furu, bok. Kostnadsfritt hembesök, fast offert inom 48h, 10 års garanti och ROT-avdrag. Malmö, Lund, Helsingborg."
    );
    setMeta("robots", "index,follow");

    // JSON-LD för Google Rich Results
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.text = JSON.stringify(buildFaqJsonLd());
    faqScript.id = "tk-faq-jsonld";
    const bizScript = document.createElement("script");
    bizScript.type = "application/ld+json";
    bizScript.text = JSON.stringify(buildLocalBusinessJsonLd());
    bizScript.id = "tk-biz-jsonld";
    document.head.appendChild(faqScript);
    document.head.appendChild(bizScript);

    return () => {
      document.title = prevTitle;
      document.getElementById("tk-faq-jsonld")?.remove();
      document.getElementById("tk-biz-jsonld")?.remove();
    };
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads/customer-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Kunde inte skicka just nu");
      }
      setSubmitted(true);
      // Scrolla upp till bekräftelsen så mobilanvändare ser den
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message || "Något gick fel. Ring oss istället.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-app text-text-primary" data-testid="tratrappor-skane-page">
      {/* STICKY MOBILE CALL-BAR */}
      <a
        href={`tel:${CONTACT_PHONE_TEL}`}
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-brand text-white py-4 text-center font-semibold shadow-2xl flex items-center justify-center gap-2"
        data-testid="sticky-call-bar"
      >
        <Phone size={20} weight="fill" />
        Ring nu — {CONTACT_PHONE_DISPLAY}
      </a>

      {/* HEADER */}
      <header className="border-b border-app bg-app/95 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading text-xl tracking-tight"
            data-testid="landing-logo"
          >
            Trappkonsulten<span className="text-brand">.</span>
          </Link>
          <a
            href={`tel:${CONTACT_PHONE_TEL}`}
            className="hidden md:flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            data-testid="header-call-btn"
          >
            <Phone size={16} weight="fill" />
            {CONTACT_PHONE_DISPLAY}
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2e25] via-[#22392e] to-[#1a2e25]" />
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: "radial-gradient(circle at 25% 20%, #c25934 0%, transparent 45%), radial-gradient(circle at 75% 80%, #d9a679 0%, transparent 50%)",
        }} />
        <div className="relative mx-auto max-w-6xl px-6 pt-14 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-2 gap-14 items-center text-white">
          <div>
            <span
              className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase text-white/70 mb-6"
              data-testid="hero-eyebrow"
            >
              <MapPin size={14} weight="fill" className="text-brand" />
              Skåne · Måttbeställda trappor sedan 1998
            </span>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
              data-testid="hero-title"
            >
              Trätrappor i Skåne —
              <br />
              <span className="text-brand">byggda för generationer.</span>
            </h1>
            <p
              className="mt-6 text-lg text-white/80 max-w-lg leading-relaxed"
              data-testid="hero-subtitle"
            >
              Måttbeställda trappor i ek, ask, furu eller bok. Kostnadsfritt
              hembesök inom Skåne, fast offert inom 48 timmar och 10 års
              garanti — utan säljprat.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#offert"
                className="bg-brand hover:bg-brand-hover text-white px-6 py-3.5 rounded-md font-medium flex items-center gap-2 transition-colors shadow-lg"
                data-testid="hero-cta-quote"
              >
                Få gratis offert
                <ArrowRight size={18} weight="bold" />
              </a>
              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-6 py-3.5 rounded-md font-medium flex items-center gap-2 transition-all backdrop-blur"
                data-testid="hero-cta-call"
              >
                <Phone size={18} weight="fill" />
                Ring direkt
              </a>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
              <span className="flex items-center gap-1.5">
                <CheckCircle size={16} weight="fill" className="text-brand" />
                Kostnadsfritt hembesök
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle size={16} weight="fill" className="text-brand" />
                Fast offert inom 48h
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle size={16} weight="fill" className="text-brand" />
                ROT-avdrag ingår
              </span>
            </div>
          </div>

          {/* HERO CARD — social proof */}
          <div className="relative" data-testid="hero-visual">
            <div className="absolute -inset-6 bg-brand/20 rounded-3xl blur-2xl" />
            <div className="relative bg-white/95 text-text-primary rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={20} weight="fill" className="text-brand" />
                ))}
                <span className="ml-2 text-sm font-medium">4,9 / 5 · 87 recensioner</span>
              </div>
              <p className="text-lg leading-relaxed italic">
                "Trappan blev exakt som vi ville — och till fast pris. Paul
                mätte upp själv, ritade förslag på plats och montaget tog
                bara en dag. Bättre än vi vågat hoppas."
              </p>
              <div className="mt-4 pt-4 border-t border-app text-sm text-text-muted">
                <div className="font-medium text-text-primary">Familjen Lundqvist</div>
                <div>Lomma · Ektrappa L-sväng · 2025</div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-heading text-brand">27</div>
                  <div className="text-xs text-text-muted">års erfarenhet</div>
                </div>
                <div>
                  <div className="text-2xl font-heading text-brand">400+</div>
                  <div className="text-xs text-text-muted">trappor i Skåne</div>
                </div>
                <div>
                  <div className="text-2xl font-heading text-brand">10 år</div>
                  <div className="text-xs text-text-muted">garanti</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="mx-auto max-w-6xl px-6 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight" data-testid="benefits-title">
            Därför väljer skåningar oss
          </h2>
          <p className="mt-4 text-text-muted">
            Vi bygger inte snabbast eller billigast. Vi bygger bäst — och står
            för det med 10 års garanti på varje trappa som lämnar verkstan.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={i}
                className="bg-surface border border-app rounded-xl p-7 hover:shadow-lg transition-shadow"
                data-testid={`benefit-card-${i}`}
              >
                <div className="w-12 h-12 rounded-lg bg-brand/10 flex items-center justify-center mb-5">
                  <Icon size={26} weight="duotone" className="text-brand" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{b.title}</h3>
                <p className="text-sm text-text-muted leading-relaxed">{b.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#f5f2ed] py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] tracking-widest uppercase text-brand font-semibold mb-3">
              Från idé till färdig trappa
            </span>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight" data-testid="process-title">
              Fyra steg. Noll överraskningar.
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => (
              <div key={i} className="relative bg-white rounded-xl p-6 shadow-sm" data-testid={`process-step-${i}`}>
                <div className="absolute -top-4 left-6 w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center font-heading text-lg shadow-lg">
                  {p.step}
                </div>
                <h3 className="text-base font-semibold mt-4 mb-2">{p.title}</h3>
                <p className="text-sm text-text-muted leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="offert" className="mx-auto max-w-4xl px-6 py-20 md:py-28 scroll-mt-20">
        {submitted ? (
          <div
            className="bg-brand/5 border-2 border-brand rounded-2xl p-10 text-center"
            data-testid="quote-success"
          >
            <CheckCircle size={56} weight="fill" className="text-brand mx-auto mb-4" />
            <h2 className="font-heading text-3xl mb-3">Tack — din förfrågan är på väg!</h2>
            <p className="text-text-muted max-w-lg mx-auto leading-relaxed">
              Vi hör av oss inom <strong>4 timmar</strong> på vardagar och
              inom ett dygn på helger. Du får ett samtal från Paul personligen
              på <strong>{form.phone}</strong> för att boka hembesök.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                className="bg-brand hover:bg-brand-hover text-white px-5 py-3 rounded-md font-medium flex items-center gap-2"
                data-testid="success-call-btn"
              >
                <Phone size={16} weight="fill" />
                Ring oss direkt
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="border border-app hover:bg-surface-hover px-5 py-3 rounded-md font-medium flex items-center gap-2"
              >
                <EnvelopeSimple size={16} weight="fill" />
                Skicka fler bilder
              </a>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1.2fr_1fr] gap-10">
            <div>
              <span className="inline-block text-[11px] tracking-widest uppercase text-brand font-semibold mb-3">
                Prisförfrågan
              </span>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight" data-testid="quote-title">
                Få en fast offert
                <br />
                <span className="text-brand">inom 48 timmar.</span>
              </h2>
              <p className="mt-5 text-text-muted leading-relaxed">
                Fyll i formuläret så ringer vi upp för att boka ett
                kostnadsfritt hembesök. Ingen försäljning — bara raka besked
                om vad din trappa kostar och när den kan vara på plats.
              </p>

              <ul className="mt-6 space-y-2.5 text-sm">
                <li className="flex items-start gap-2">
                  <Clock size={18} weight="fill" className="text-brand mt-0.5 shrink-0" />
                  <span>Svar inom 4 timmar på vardagar</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck size={18} weight="fill" className="text-brand mt-0.5 shrink-0" />
                  <span>Ingen anmälan, inget kort — helt gratis</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChatCircleText size={18} weight="fill" className="text-brand mt-0.5 shrink-0" />
                  <span>Vi ringer, vi säljer inte via mail</span>
                </li>
              </ul>
            </div>

            <form
              onSubmit={submit}
              className="bg-surface border border-app rounded-2xl p-6 md:p-7 shadow-sm space-y-4"
              data-testid="quote-form"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">Namn *</label>
                  <input
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="quote-input-name"
                    placeholder="Ditt namn"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">Telefon *</label>
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="quote-input-phone"
                    placeholder="070-XXX XX XX"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">E-post *</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="quote-input-email"
                    placeholder="din@epost.se"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">Postnr</label>
                  <input
                    value={form.postal_code}
                    onChange={update("postal_code")}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="quote-input-postal"
                    placeholder="212 34"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">Ort</label>
                  <input
                    value={form.city}
                    onChange={update("city")}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="quote-input-city"
                    placeholder="Malmö"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">Typ av trappa</label>
                  <select
                    value={form.stair_type}
                    onChange={update("stair_type")}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="quote-input-stair-type"
                  >
                    {STAIR_TYPES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">Material</label>
                  <select
                    value={form.material}
                    onChange={update("material")}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="quote-input-material"
                  >
                    {MATERIALS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">Tidsram</label>
                  <select
                    value={form.timeframe}
                    onChange={update("timeframe")}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    data-testid="quote-input-timeframe"
                  >
                    {TIMEFRAMES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1.5 text-text-muted">Meddelande (valfritt)</label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={3}
                    className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40 resize-none"
                    data-testid="quote-input-message"
                    placeholder="Antal steg, våningshöjd, önskad räckesstil…"
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="quote-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-medium py-3.5 rounded-md flex items-center justify-center gap-2 transition-colors"
                data-testid="quote-submit-btn"
              >
                {submitting ? "Skickar..." : "Skicka prisförfrågan"}
                {!submitting && <ArrowRight size={18} weight="bold" />}
              </button>

              <p className="text-xs text-text-muted text-center leading-relaxed">
                Genom att skicka godkänner du att vi ringer/mailar dig om din
                förfrågan. Vi delar aldrig dina uppgifter med tredje part.
              </p>
            </form>
          </div>
        )}
      </section>

      {/* SOCIAL PROOF - CITIES */}
      <section className="bg-[#1a2e25] text-white py-16">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <div className="flex items-center justify-center gap-2 text-white/60 text-xs tracking-widest uppercase mb-4">
            <Hammer size={14} weight="fill" className="text-brand" />
            Vi bygger i hela Skåne
          </div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8">
            400+ monterade trappor sedan 1998
          </h2>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm text-white/80" data-testid="cities-list">
            {CITIES.map((c) => (
              <span key={c} className="hover:text-brand transition-colors">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 py-20 md:py-28">
        <div className="text-center mb-12">
          <span className="inline-block text-[11px] tracking-widest uppercase text-brand font-semibold mb-3">
            Vanliga frågor
          </span>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight" data-testid="faq-title">
            Det ni brukar undra över
          </h2>
        </div>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <div
              key={i}
              className="bg-surface border border-app rounded-xl overflow-hidden"
              data-testid={`faq-item-${i}`}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-surface-hover transition-colors"
              >
                <span className="font-medium">{f.q}</span>
                <span className={`text-brand text-xl transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5 text-sm text-text-muted leading-relaxed">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand text-white py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight mb-5" data-testid="final-cta-title">
            Redo att börja bygga din trappa?
          </h2>
          <p className="text-white/90 max-w-xl mx-auto mb-8 leading-relaxed">
            Kostnadsfritt hembesök inom Skåne. Fast offert inom 48 timmar.
            Ingen anmälan — bara ett samtal med Paul.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="#offert"
              className="bg-white text-brand hover:bg-white/95 px-6 py-3.5 rounded-md font-semibold flex items-center gap-2"
              data-testid="final-cta-form"
            >
              Skicka prisförfrågan
              <ArrowRight size={18} weight="bold" />
            </a>
            <a
              href={`tel:${CONTACT_PHONE_TEL}`}
              className="bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur px-6 py-3.5 rounded-md font-semibold flex items-center gap-2"
              data-testid="final-cta-call"
            >
              <Phone size={18} weight="fill" />
              Eller ring direkt
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-app bg-app pb-24 md:pb-8">
        <div className="mx-auto max-w-6xl px-6 py-10 grid md:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="font-heading text-lg mb-2">Trappkonsulten<span className="text-brand">.</span></div>
            <p className="text-text-muted leading-relaxed">
              Måttbeställda trätrappor i Skåne. 27 års erfarenhet,
              400+ monterade trappor och 10 års garanti.
            </p>
          </div>
          <div>
            <div className="font-medium mb-2">Kontakt</div>
            <div className="space-y-1.5 text-text-muted">
              <a href={`tel:${CONTACT_PHONE_TEL}`} className="block hover:text-brand transition-colors">
                {CONTACT_PHONE_DISPLAY}
              </a>
              <a href={`mailto:${CONTACT_EMAIL}`} className="block hover:text-brand transition-colors">
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
          <div>
            <div className="font-medium mb-2">Länkar</div>
            <div className="space-y-1.5 text-text-muted">
              <Link to="/" className="block hover:text-brand transition-colors">Hem</Link>
              <Link to="/privacy" className="block hover:text-brand transition-colors">Integritetspolicy</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-app py-5 text-center text-xs text-text-muted">
          © {new Date().getFullYear()} Trappkonsulten · Alla rättigheter förbehållna
        </div>
      </footer>
    </div>
  );
}
